import { getJson, writeEntity } from '@/lib/entity-api';
import type { PurchaseOrderBilling } from '../lib/bill-lines';

/**
 * Bills <-> purchase orders (many-to-many). Every read and write goes through the API; the server decides what is "related"
 * (live documents of the caller's company only) and refuses a link to another company's order.
 */
export interface RelatedPurchaseOrder {
  id: string;
  po_number: string;
  title: string | null;
  status: string;
  amount: number;
  project_id: string;
}

export interface RelatedBill {
  id: string;
  bill_id: string;
  name: string | null;
  status: string;
  total_amount: number;
  project_id: string;
}

const idsParam = (ids: string[]) => encodeURIComponent(ids.join(','));

/** The purchase orders linked to each bill: { [billId]: [...] }. */
export function fetchRelatedPurchaseOrders(billIds: string[]): Promise<Record<string, RelatedPurchaseOrder[]>> {
  if (billIds.length === 0) return Promise.resolve({});
  return getJson(`api/bills/related-purchase-orders?ids=${idsParam(billIds)}`, 'Failed to load the purchase orders of the bill');
}

/** The (live) bills linked to each purchase order: { [purchaseOrderId]: [...] }. */
export function fetchRelatedBills(purchaseOrderIds: string[]): Promise<Record<string, RelatedBill[]>> {
  if (purchaseOrderIds.length === 0) return Promise.resolve({});
  return getJson(`api/purchase-orders/related-bills?ids=${idsParam(purchaseOrderIds)}`, 'Failed to load the bills of the purchase order');
}

/**
 * What has been billed of a purchase order across ALL its valid bills, and what is left (per line, tax and other): the base of Full Amount,
 * Split proportionally and the reconciliation. `excludeBillId` leaves out the bill being edited.
 */
export function fetchPurchaseOrderBilling(purchaseOrderId: string, excludeBillId?: string): Promise<PurchaseOrderBilling> {
  const query = excludeBillId ? `?exclude_bill_id=${encodeURIComponent(excludeBillId)}` : '';
  return getJson(`api/purchase-orders/${purchaseOrderId}/billing${query}`, 'Failed to load what has been billed of the purchase order');
}

/** The bill's purchase orders become exactly `purchaseOrderIds` (one request; the server writes only the difference). */
export function replaceBillPurchaseOrders(billId: string, purchaseOrderIds: string[]): Promise<{ added: number; removed: number }> {
  return writeEntity(`api/bills/${billId}/purchase-orders`, 'PUT', { purchase_order_ids: purchaseOrderIds }, 'Failed to link the purchase orders to the bill');
}

/**
 * The confirmation shown before deleting purchase orders that have bills: the bills are KEPT, they only lose the link to the order.
 * One line per purchase order.
 */
export function relatedBillsNotice(withBills: Array<{ po_number: string; title?: string | null; bills: Array<{ bill_id: string }> }>): string {
  return withBills
    .map(({ po_number, title, bills }) => {
      const many = bills.length > 1;
      const codes = bills.map((b) => b.bill_id).sort().join(', ');
      const label = title ? `${po_number} (${title})` : po_number;
      return `Purchase order ${label} has ${bills.length} related bill${many ? 's' : ''} (${codes}). ${many ? 'The bills' : 'The bill'} will be kept, but will lose the link to this purchase order.`;
    })
    .join('\n');
}
